import { TRPCErrorBoundary } from '@client/components/_core/error-handling/TRPCErrorBoundary';
import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { Space } from '@client/components/_ui/layout';
import { ReadOnlyBanner } from '@client/components/admin/ReadOnlyBanner';
import { RequestSupportSessionDrawer } from '@client/components/admin-console/support-session/RequestSupportSessionDrawer';
import { getEmailVerificationStatus } from '@client/components/domain-auth/emailVerificationStatus';
import { getConfig } from '@client/config/runtime';
import { useAdminModulePermissions } from '@client/hooks/admin/useAdminModulePermissions';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useUser } from '@client/hooks/business/user/useUser';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { createColumnHelper, PrismaTable, type PrismaTableColumn, usePrismaTable } from '@client/hooks/table-v2';
import { useDeleteConfirmation } from '@client/hooks/ui/useDeleteConfirmation';
import { useFeatureFlag } from '@client/hooks/useFeatureFlag';
import type { RouterInputs, RouterOutputs } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE, resolveEffectiveAuthenticatedDomain } from '@shared/constants';
import { createFileRoute } from '@tanstack/react-router';
import { Dropdown, Tag } from 'antd';
import { ChevronDown, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import invariant from 'tiny-invariant';
import { EmailVerificationDriftCard } from './EmailVerificationDriftCard';
import { type OrganizationFilterState, OrganizationFilters } from './OrganizationFilters';

export const Route = createFileRoute('/admin-console/organisation-management/organisations/')({
    staticData: {
        ui: {
            title: 'Organisations',
            subtitle: 'Organisations can be configured here.',
            menuDescription: 'View and manage all organisations',
        },
    },
    component: AdminPageOrganizations,
});

type QueryVariables = RouterInputs['admin']['org']['listOrgs'];
type OrgRow = NonNullable<RouterOutputs['admin']['org']['listOrgs']>['rows'][number];
type OrgOrderBy = NonNullable<QueryVariables['orderBy']>;

function useOrgTableColumns(
    navigate: ReturnType<typeof useNavigate>,
    setOrgIdToDelete: (orgId: bigint) => void,
    showConfirmationModal: (orgName: string) => void,
    currentUser: ReturnType<typeof useUser>,
    canEdit: boolean,
    setSupportSessionOrg: (org: { id: bigint; name: string } | null) => void,
    onDeleteDemoOrg: (orgId: bigint) => void,
): readonly PrismaTableColumn<OrgRow, OrgOrderBy, any>[] {
    const columnHelper = createColumnHelper<OrgRow, OrgOrderBy>();

    return [
        columnHelper.accessor('id', {
            id: 'id',
            header: 'ID',
            sortable: 'id',
            cell: ({ value }) => BigInt(value).toString(),
            align: 'center',
        }),
        columnHelper.accessor('name', {
            id: 'name',
            header: 'Name',
            sortable: 'name',
            className: 'whitespace-nowrap',
        }),
        columnHelper.accessor('isEnabled', {
            id: 'isEnabled',
            header: 'Is enabled?',
            sortable: 'isEnabled',
            cell: ({ value }) =>
                value ? (
                    <Tag variant="outlined" color="success">
                        Yes
                    </Tag>
                ) : (
                    <Tag variant="outlined" color="error">
                        No
                    </Tag>
                ),
        }),
        columnHelper.accessor('emailId', {
            id: 'emailId',
            header: 'Email ID',
            sortable: 'emailId',
            cell: ({ value }) => (value ? <Tag variant="outlined">{value}</Tag> : ''),
        }),
        columnHelper.accessor('authenticatedDomain', {
            id: 'authenticatedDomain',
            header: 'Authenticated domain',
            sortable: 'authenticatedDomain',
            cell: ({ row }) => {
                const effectiveAuthenticatedDomain = resolveEffectiveAuthenticatedDomain(
                    row.senderEmail,
                    row.authenticatedDomain,
                );

                if (!effectiveAuthenticatedDomain) {
                    return null;
                }

                const status = getEmailVerificationStatus(
                    row.senderEmail,
                    row.isEmailDomainVerified,
                    row.authenticatedDomain,
                );

                if (status.kind !== 'verified' && status.kind !== 'pending') {
                    return null;
                }

                return (
                    <div className="flex items-center gap-1.5 whitespace-nowrap">
                        <span>{effectiveAuthenticatedDomain}</span>
                        <Tag variant="outlined" color={status.kind === 'verified' ? 'success' : 'warning'}>
                            {status.kind === 'verified' ? 'Verified' : 'Pending'}
                        </Tag>
                    </div>
                );
            },
        }),
        columnHelper.accessor('enabledModules', {
            id: 'modules',
            header: 'Modules',
            sortable: false,
            cell: ({ row }) =>
                row.enabledModules.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                        {row.enabledModules.map((module) => (
                            <Tag variant="outlined" key={module} color="blue">
                                {module}
                            </Tag>
                        ))}
                    </div>
                ) : (
                    <Tag variant="outlined" color="default">
                        None
                    </Tag>
                ),
        }),
        columnHelper.accessor('lastLoginAt', {
            id: 'lastLoginAt',
            header: 'Last login',
            sortable: 'lastLoginAt',
            cell: ({ value }) => (value ? value.toLocaleString() : 'Never'),
            className: 'whitespace-nowrap',
        }),
        columnHelper.accessor('markedForDeletion', {
            id: 'markedForDeletion',
            header: 'Marked for deletion?',
            sortable: 'markedForDeletion',
            cell: ({ value }) =>
                value ? (
                    <Tag variant="outlined" color="error">
                        Yes
                    </Tag>
                ) : (
                    <Tag variant="outlined" color="success">
                        No
                    </Tag>
                ),
        }),
        columnHelper.accessor('createdAt', {
            id: 'createdAt',
            header: 'Created at',
            sortable: 'createdAt',
            cell: ({ value }) => value.toLocaleString(),
            className: 'whitespace-nowrap',
        }),
        columnHelper.actions({
            cell: ({ row: record }) => (
                <Space size="small">
                    <Button.Edit.Link
                        to="/admin-console/organisation-management/organisations/$orgId"
                        params={{ orgId: record.id }}
                    />
                    {canEdit && (
                        <>
                            <Dropdown
                                trigger={['click']}
                                menu={{
                                    items: [
                                        {
                                            key: 'users',
                                            label: 'Manage users',
                                            onClick: () =>
                                                navigate({
                                                    to: '/admin-console/organisation-management/organisations/$orgId/users',
                                                    params: { orgId: record.id },
                                                }),
                                        },
                                        {
                                            key: 'wallet',
                                            label: 'Manage wallet',
                                            onClick: () =>
                                                navigate({
                                                    to: '/admin-console/organisation-management/organisations/$orgId/wallet',
                                                    params: { orgId: record.id },
                                                }),
                                        },
                                        {
                                            key: 'subscriptions',
                                            label: 'Subscriptions',
                                            onClick: () =>
                                                navigate({
                                                    to: '/admin-console/organisation-management/organisations/$orgId/subscriptions',
                                                    params: { orgId: record.id },
                                                }),
                                        },
                                        { type: 'divider' },
                                        {
                                            key: 'supportSession',
                                            label: 'Request Support Session',
                                            onClick: () => setSupportSessionOrg({ id: record.id, name: record.name }),
                                        },
                                    ],
                                }}
                            >
                                <Button type="tertiary" icon={<ChevronDown />} iconPosition="end">
                                    Shortcuts
                                </Button>
                            </Dropdown>

                            {record.isDemoOrg ? (
                                <Button.Delete
                                    onConfirm={() => onDeleteDemoOrg(record.id)}
                                    confirmTitle="Delete demo org"
                                    confirmDescription="Disable this demo tenant and mark it for deletion. No name confirmation — demo orgs are throwaway."
                                />
                            ) : (
                                (currentUser?.roles.includes('super_admin') ||
                                    getConfig('ENV', import.meta.env.VITE_ENV) !== 'production') && (
                                    <Button.Icon
                                        icon={<Trash2 />}
                                        color="red"
                                        onClick={() => {
                                            setOrgIdToDelete(record.id);
                                            showConfirmationModal(record.name);
                                        }}
                                    />
                                )
                            )}
                        </>
                    )}
                </Space>
            ),
        }),
    ] as const;
}

function AdminPageOrganizations() {
    const { trpc, trpcClient } = useTrpc();
    const currentUser = useUser();
    const navigate = useNavigate();
    const { canEdit } = useAdminModulePermissions('orgs');
    const demoOrgTemplatesEnabled = useFeatureFlag('admin-demo-org-templates', false);

    const [orgIdToDelete, setOrgIdToDelete] = useState<bigint | undefined>();
    const [filterWhere, setFilterWhere] = useState<QueryVariables['where']>({});
    const [domainVerificationStatusFilters, setDomainVerificationStatusFilters] = useState<
        NonNullable<QueryVariables['domainVerificationStatusFilters']>
    >([]);
    const [supportSessionOrg, setSupportSessionOrg] = useState<{ id: bigint; name: string } | null>(null);

    // Use usePathState to persist table filtering, sorting, and pagination settings
    const [queryVariables, setQueryVariables] = usePathState<RouterInputs['admin']['org']['listOrgs']>(
        'queryVariables',
        {
            orderBy: [{ id: 'desc' }],
            limit: DEFAULT_PAGE_SIZE,
        } as RouterInputs['admin']['org']['listOrgs'],
    );

    const {
        data: orgs,
        isFetching,
        refetch,
        error,
    } = trpc.admin.org.listOrgs.useQuery({
        ...queryVariables,
        domainVerificationStatusFilters,
        where: filterWhere,
    });

    const { showConfirmationModal, contextHolder: deleteConfirmationModal } = useDeleteConfirmation({
        entityDisplayName: 'organisation',
        async onConfirm(ctx) {
            try {
                invariant(orgIdToDelete, 'orgIdToDelete is required');
                message.loading('Deleting organisation...');
                await trpcClient.admin.org.deleteOrg.mutate({
                    orgId: BigInt(orgIdToDelete),
                    orgName: ctx.confirmationText,
                });
                message.success('Organisation disabled and marked for deletion');
                refetch();
            } catch (error) {
                errorMessage.show(error);
            }
        },
    });

    const handleDeleteDemoOrg = React.useCallback(
        async (orgId: bigint) => {
            try {
                message.loading('Deleting demo org...');
                await trpcClient.admin.demoOrg.deleteDemoOrg.mutate({ orgId });
                message.success('Demo org disabled and marked for deletion');
                refetch();
            } catch (error) {
                errorMessage.show(error);
            }
        },
        [trpcClient, refetch],
    );

    const columns = useOrgTableColumns(
        navigate,
        setOrgIdToDelete,
        showConfirmationModal,
        currentUser,
        canEdit,
        setSupportSessionOrg,
        handleDeleteDemoOrg,
    );

    const handleFilterChange = React.useCallback(
        ({ where, domainVerificationStatusFilters }: OrganizationFilterState) => {
            setFilterWhere(where);
            setDomainVerificationStatusFilters(domainVerificationStatusFilters);
        },
        [],
    );

    const tableProps = usePrismaTable({
        data: orgs?.rows || [],
        total: orgs?.total || 0,
        loading: isFetching,
        columns,
        rowKey: 'id',
        queryParams: queryVariables,
        onParamsChange: setQueryVariables,
        pagination: {
            position: 'both',
            showSizeChanger: true,
        },
        columnVisibility: {
            enabled: true,
            persistKey: 'organizations-table-columns',
            enableReordering: true,
        },
        refreshable: {
            enabled: true,
            onRefresh: () => refetch(),
        },
    });

    return (
        <PageController route={Route}>
            <ReadOnlyBanner module="orgs" />
            <TRPCErrorBoundary error={error}>
                {deleteConfirmationModal}

                <EmailVerificationDriftCard canEdit={canEdit} />

                <TableToolbar
                    filters={<OrganizationFilters onChange={handleFilterChange} />}
                    actions={
                        demoOrgTemplatesEnabled && canEdit ? (
                            <Button.Link
                                variant="secondary"
                                to="/admin-console/organisation-management/new-from-template"
                            >
                                Create demo org
                            </Button.Link>
                        ) : undefined
                    }
                    variant="enhanced"
                />

                <PrismaTable {...tableProps} />
            </TRPCErrorBoundary>

            <RequestSupportSessionDrawer
                open={supportSessionOrg !== null}
                onClose={() => setSupportSessionOrg(null)}
                orgId={supportSessionOrg?.id ?? BigInt(0)}
                orgName={supportSessionOrg?.name ?? ''}
            />
        </PageController>
    );
}
